/**
 * <s-polaroid-shuffle> — swaps a pair of stacked polaroids on a timer.
 *
 * Toggles [data-swapped] on itself every `data-interval` ms (default 4000); the markup's
 * group-data-[swapped] utilities move the back card to the front. Each card's
 * [data-progress-fill] ring fills across the interval so the front card shows when the
 * next shuffle is due. Tapping or clicking the stack swaps straight away and restarts
 * the timer. With reduced motion the timer never starts and tapping still swaps.
 */
class SPolaroidShuffle extends HTMLElement {
  #ac;
  #timer = 0;

  get #interval() {
    const value = Number.parseInt(this.dataset.interval ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value : 4000;
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#ac.signal });
    this.#start();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #onClick = () => {
    this.#swap();
  };

  #swap = () => {
    this.toggleAttribute('data-swapped');
    this.#start();
  };

  #start() {
    window.clearTimeout(this.#timer);
    const fills = this.querySelectorAll('[data-progress-fill]');

    if (this.#reducedMotion) {
      fills.forEach((fill) => {
        fill.style.transition = 'none';
        fill.style.strokeDashoffset = '100';
      });
      return;
    }

    const interval = this.#interval;
    fills.forEach((fill) => {
      fill.style.transition = 'none';
      fill.style.strokeDashoffset = '100';
    });
    // Force a reflow so the reset applies before the fill transition starts.
    void this.offsetWidth;
    fills.forEach((fill) => {
      fill.style.transition = `stroke-dashoffset ${interval}ms linear`;
      fill.style.strokeDashoffset = '0';
    });

    this.#timer = window.setTimeout(this.#swap, interval);
  }
}

if (!customElements.get('s-polaroid-shuffle')) {
  customElements.define('s-polaroid-shuffle', SPolaroidShuffle);
}
